import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import clsx from 'clsx';
import { Loader2 } from 'lucide-react';
import { spring, tap } from './motion';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover shadow-primary',
  secondary: 'bg-surface text-ink border border-line hover:bg-raised shadow-sm',
  outline: 'bg-transparent text-primary-ink border border-primary/40 hover:bg-primary-soft',
  ghost: 'bg-transparent text-ink hover:bg-raised',
  soft: 'bg-primary-soft text-primary-ink hover:bg-primary/15',
  danger: 'bg-danger text-white hover:brightness-110 shadow-sm',
};
const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[0.8125rem] gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-ui gap-2 rounded-xl',
  lg: 'h-12 px-5 text-[0.9375rem] gap-2 rounded-xl',
};

type BaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  block?: boolean;
};

export type ButtonProps = BaseProps & Omit<HTMLMotionProps<'button'>, 'children'> & { children?: ReactNode };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, iconRight, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <motion.button
      ref={ref}
      type={type}
      whileTap={disabled || loading ? undefined : tap}
      transition={spring}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55',
        variants[variant],
        sizes[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
      {iconRight}
    </motion.button>
  );
});

type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  label: string;
  icon: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  active?: boolean;
  variant?: 'ghost' | 'secondary' | 'primary';
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', active, variant = 'ghost', className, type = 'button', ...rest },
  ref,
) {
  const s = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-xl transition-colors active:scale-[0.96] disabled:opacity-50',
        s,
        variant === 'ghost' && (active ? 'bg-primary-soft text-primary-ink' : 'text-muted hover:bg-raised hover:text-ink'),
        variant === 'secondary' && 'border border-line bg-surface text-ink hover:bg-raised',
        variant === 'primary' && 'bg-primary text-primary-fg hover:bg-primary-hover',
        className,
      )}
      {...rest}
    >
      {icon}
    </button>
  );
});
